import React, { useState, useEffect, useRef } from "react";
import { ApiService, CoveragePhaseService } from "../../../../api";
import { SidePanel, SidePanelContent, CustomButton, icon } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import styles from "./PhaseHistoryPanel.module.css";

const OPERATIONS = {
	ADD: "ADD",
	UPDATE: "UPDATE",
	DELETE: "DELETE",
	EXPORT: "EXPORT",
	IMPORT: "IMPORT",
	LOCK: "LOCK",
	UNLOCK: "UNLOCK",
	ARCHIVE: "ARCHIVE",
	UNARCHIVE: "UNARCHIVE",
};

const formatHistoryEntry = (entry) => {
	const userName = entry.username || translate("common.unknown-user");
	const dateObj = new Date(entry.date);
	const date = dateObj.toLocaleDateString("fr-FR", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});
	const time = dateObj
		.toLocaleTimeString("fr-FR", {
			hour: "numeric",
			minute: "2-digit",
			hour12: false,
		})
		.replace(":", "h");
	let actionText;
	switch (entry.operation) {
		case OPERATIONS.ADD:
			actionText = translate("project-phases.history.created-phase");
			break;
		case OPERATIONS.UPDATE:
			actionText = translate("project-phases.history.updated-phase");
			break;
		case OPERATIONS.DELETE:
			actionText = translate("project-phases.history.deleted-phase");
			break;
		case OPERATIONS.EXPORT:
			actionText = translate("project-phases.history.exported-strategy");
			break;
		case OPERATIONS.IMPORT:
			actionText = translate("project-phases.history.imported-strategy");
			break;
		case OPERATIONS.LOCK:
			actionText = translate("project-phases.history.locked-phase");
			break;
		case OPERATIONS.UNLOCK:
			actionText = translate("project-phases.history.unlocked-phase");
			break;
		case OPERATIONS.ARCHIVE:
			actionText = translate("project-phases.history.archived-phase");
			break;
		case OPERATIONS.UNARCHIVE:
			actionText = translate("project-phases.history.unarchived-phase");
			break;
		default:
			actionText = translate("project-phases.history.modified-phase");
	}
	return {
		...entry,
		formattedText: `${userName} ${actionText}`,
		date,
		time,
	};
};

const HistoryContent = ({ isLoading, historyEntries, onDownload }) => {
	if (isLoading) {
		return <div className={styles.loading}>{translate("common:results.loading")}...</div>;
	}

	if (historyEntries.length === 0) {
		return <div className={styles.emptyState}>{translate("project-phases.history.no-history")}</div>;
	}

	return historyEntries.map((entry) => (
		<div
			key={`${entry.date || Date.now()}-${entry.operation || "unknown"}-${entry.username || "user"}`}
			className={styles.historyEntry}
		>
			<div className={styles.entryContent}>
				<div className={styles.entryDate}>
					{entry.date} {entry.time}
				</div>
				<div className={styles.entryText}>{entry.formattedText}</div>
			</div>

			{entry.operation === OPERATIONS.EXPORT && (
				<CustomButton
					className={styles.downloadButton}
					color="primary"
					size="xs"
					startIcon={icon.faDownload}
					variant="outlined"
					onClick={() => onDownload(entry)}
				>
					{translate("project-phases.history.download")}
				</CustomButton>
			)}
		</div>
	));
};

const PhaseHistoryPanel = ({ open, onClose, phase, projectId }) => {
	const [historyEntries, setHistoryEntries] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const cancelTokenSourceRef = useRef(null);

	useEffect(() => {
		if (open && phase?.id) {
			cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
			setIsLoading(true);

			CoveragePhaseService.getCoveragePhaseHistory(
				{ projectId, phaseId: phase.id },
				cancelTokenSourceRef.current.token
			)
				.then((data) => {
					const formattedEntries = (data || [])
						.map(formatHistoryEntry)
						.sort((a, b) => new Date(b.date) - new Date(a.date));
					setHistoryEntries(formattedEntries);
				})
				.catch(() => {
					setHistoryEntries([]);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
		return () => {
			if (cancelTokenSourceRef.current) {
				ApiService.cancelTokens(cancelTokenSourceRef.current);
			}
		};
	}, [open, phase.id, projectId]);
	const handleDownloadExportedStrategy = (entry) => {
		CoveragePhaseService.downloadExportedStrategy(
			{
				projectId,
				exportId: entry.exportId,
			},
			cancelTokenSourceRef.current.token
		)
			.then((response) => {
				const contentDisposition = response.headers?.["content-disposition"];
				let filename = `coverage_export_${entry.date}.xlsx`;

				if (contentDisposition) {
					const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
					if (filenameMatch && filenameMatch[1]) {
						filename = filenameMatch[1].replace(/['"]/g, "");
					}
				}
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			})
			.catch((error) => {
				console.error("Download failed:", error);
			});
	};
	return (
		<SidePanel open={open} size={45} onClose={onClose}>
			<SidePanelContent title={`${phase.name}${translate("project-phases.history.title")}`} onClose={onClose}>
				<div className={styles.container}>
					<div className={styles.historyList}>
						<HistoryContent
							historyEntries={historyEntries}
							isLoading={isLoading}
							onDownload={handleDownloadExportedStrategy}
						/>
					</div>
				</div>
			</SidePanelContent>
		</SidePanel>
	);
};

export default PhaseHistoryPanel;
